import useSWRInfinite, {SWRInfiniteResponse} from "swr/infinite";
import WatsonApi from "../../../backends/WatsonApi";
import {ReactiveContentItem} from "../SchoolFeedContentItem";
import {mutate as globalMutate} from "swr"
import {useSchoolFeedUrlQuery} from "../../../hooks/useSchoolFeedData";
import {useStores} from "../../../stores/useStores";
import {IFallbackData} from "../../../utils/SchoolBlocksUtilities";
import {convertMilesToKm} from "../../../utils/DataUtilities";

interface SchoolFeedInfiniteScrollResponse extends Omit<SWRInfiniteResponse, "data"> {
    contentItems: ReactiveContentItem[],
    showViewMore: boolean,
    getItemAtIndex: (idx: number) => Promise<ReactiveContentItem | null>,
    totalContentItems: number,
}

interface SchoolFeedApiQuery {
    page_size: number;
    expand: string;
    page: number;
    organization_id?: string | number;
    following?: boolean;
    lat?: string;
    long?: string;
    radius?: number;
    organization__is_live?: boolean;
    created_at__gte?: string;
    organization__type?: string;
    published?: boolean;
    [key: string]: any;
}

// seems like this is poorly typed, but string represents a search query which could be anything
type SchoolFeedInfiniteContentType = ISchoolFeedContentType | string;

export function getSchoolFeedInfiniteScrollUniqueKey(contentType: SchoolFeedInfiniteContentType) {
    return `organizations_schoolfeed_items_list_${contentType}`
}

export function buildCacheKey(pageIndex, contentType, uniqueQueryParameters, pathQueryParameters) {
    return JSON.stringify({
        key: getSchoolFeedInfiniteScrollUniqueKey(contentType),
        page: pageIndex,
        ...pathQueryParameters,
        organization_id: uniqueQueryParameters.hasOwnProperty("organization_id")
            ? uniqueQueryParameters["organization_id"]
            : pathQueryParameters["organization_id"],
    })
}

export function useSchoolFeedContentPathQueryParams() {
    const {organizationStore, userStore} = useStores();
    const urlQuery = useSchoolFeedUrlQuery();
    const q = {}

    if (urlQuery.lat && urlQuery.long && urlQuery.radius) {
        q["lat"] = urlQuery.lat;
        q["long"] = urlQuery.long;
        q["radius"] = convertMilesToKm(Number(urlQuery.radius));
        q["organization__is_live"] = true;


        const orgType = organizationStore.organization.type;
        if (orgType !== "school" && orgType !== "district") {
            q["organization__type"] = orgType;
        }

    } else {
        if (!urlQuery.orgId && !urlQuery.following) {
            q["organization_id"] = organizationStore.organization.id;
        } else if (!urlQuery.following) {
            q["organization_id"] = urlQuery.orgId;
        }
        if (!userStore.editor) {
            q["published"] = true;
        }
        if (urlQuery.following) {
            q["following"] = true;
        }
    }

    return q;
}

export const schoolFeedPageSize = 12;

export function useSchoolFeedInfiniteScroll(contentType: SchoolFeedInfiniteContentType,
                                            uniqueQueryParams: { [k: string]: any },
                                            fallbackData?: IFallbackData): SchoolFeedInfiniteScrollResponse {
    const pathQueryParams = useSchoolFeedContentPathQueryParams();
    const fetcher = async (key) => {
        const parsedKey = JSON.parse(key);
        const client = await WatsonApi();
        const q: SchoolFeedApiQuery = {
            page_size: schoolFeedPageSize,
            expand: "organization,discussion_prompt",
            page: parsedKey.page + 1,
            ...uniqueQueryParams,
            ...pathQueryParams,
            organization_id: parsedKey.organization_id
        }
        let result;
        if (q.organization_id && !q["following"]) {
            result = await client.apis.organizations.organizations_schoolfeed_items_list({
                organization_pk: parsedKey.organization_id,
                ...q,
            });
        } else {
            result = await client.apis.schoolfeed_items.schoolfeed_items_list(q);
        }

        return {
            contentItems: result.obj.data.map(d => new ReactiveContentItem(d)),
            total: result.obj.meta.total,
        };
    }

    const {
        data,
        isLoading,
        isValidating,
        size,
        setSize,
        error,
        mutate
    } = useSWRInfinite((pageIndex, previousPageData) => {
        return buildCacheKey(pageIndex, contentType, uniqueQueryParams, pathQueryParams);
    }, fetcher, {
        revalidateOnMount: !fallbackData,
        revalidateFirstPage: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        fallbackData: fallbackData ? [{
            total: fallbackData.total,
            contentItems: fallbackData.contentItems.map(d => new ReactiveContentItem(d)),
        }] : undefined,
    })

    /*
    This function is passed into the SocialItemModal on open, which means that it will not
    update when referenced outside variables update. Because of that, it reads from the
    cache directly, and updates the cache as necessary.
    */
    const getItemAtIndex = async function (idx) {
        const pageSize = uniqueQueryParams["page_size"] || schoolFeedPageSize;
        const page = Math.floor(idx / pageSize);
        const cacheKey = buildCacheKey(
            page,
            contentType,
            uniqueQueryParams,
            pathQueryParams,
        );
        const paginatedIndex = idx - (page * pageSize);


        // grab data from existing cache
        const cacheData = await globalMutate(cacheKey);

        if (cacheData) {
            return cacheData.contentItems[paginatedIndex];
        } else {
            // fetch new data and update the cache
            const newData = await globalMutate(cacheKey, async () => await fetcher(cacheKey));
            await setSize(page + 1);
            if (newData) {
                return newData.contentItems[paginatedIndex];
            }
        }
        return null;
    }

    const contentItems = data?.map(curr => curr.contentItems).flat() || [];
    const showViewMore = data?.[0].total > contentItems.length;
    const total = data?.[0].total;

    return {
        contentItems,
        totalContentItems: total,
        getItemAtIndex,
        showViewMore,
        isLoading,
        isValidating,
        size,
        setSize,
        error,
        mutate,
    }
}
